import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, RotateCcw, Zap, Settings, Map, PlayCircle } from "lucide-react";
import StudySession from "@/components/StudySession";
import Tutorial, { isTutorialDone } from "@/components/Tutorial";
import SpiralIcon from "@/components/SpiralIcon";
import {
  autoStreakOnRestDay,
  getCurrentWeekStatus,
  getDailyTasks,
  getUserSettings,
  getWeeklyStudiedDates,
  markTaskCompleted,
  saveSessionResults,
} from "@/lib/api";
import { shouldResumeAsReplay } from "@/lib/session-init";
import { haptic } from "@/lib/haptic";
import { TOTAL_ENTRIES, TOTAL_WEEKS } from "@/lib/course";
import type { DailyTask, InterruptedSession, SwipeResult, UserSettings } from "@/types";

const SESSION_STORAGE_KEY = "locomoco_interrupted_session";
const DAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

function localDate(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getCalendarWeekDays(): { date: string; label: string }[] {
  const today = new Date();
  const todayDow = today.getDay();
  const sinceMon = (todayDow + 6) % 7;
  const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - sinceMon, 12, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i, 12, 0, 0);
    return { date: localDate(d), label: DAY_LABELS[d.getDay()] };
  });
}

function getGreeting(name: string): string {
  const h = new Date().getHours();
  if (h < 6) return `こんばんは、${name}！夜型プレイヤーだね🌙`;
  if (h < 11) return `おはよう、${name}！今日も一緒にやろう🔥`;
  if (h < 17) return `こんにちは、${name}！調子はどう？💪`;
  return `こんばんは、${name}！今日も頑張ろう⭐`;
}

export default function Home() {
  const navigate = useNavigate();
  const [activeSession, setActiveSession] = useState<DailyTask | null>(null);
  const [resumeSession, setResumeSession] = useState<InterruptedSession | null>(null);
  const isRepeatModeRef = useRef(false);
  const dailyGoalMetRef = useRef(false);
  const [tasks, setTasks] = useState<DailyTask[]>([]);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [interruptedTask, setInterruptedTask] = useState<InterruptedSession | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isRestDay, setIsRestDay] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [ignorePerfectToday, setIgnorePerfectToday] = useState(false);

  useEffect(() => {
    async function init() {
      if (!isTutorialDone()) setShowTutorial(true);

      const [tasksData, settingsData] = await Promise.all([getDailyTasks(), getUserSettings()]);
      setTasks(tasksData);
      setUserSettings(settingsData);

      if (tasksData.length === 0) {
        const rest = await autoStreakOnRestDay();
        if (rest) {
          setIsRestDay(true);
          const refreshed = await getUserSettings();
          setUserSettings(refreshed);
        } else {
          const status = await getCurrentWeekStatus();
          if (status && status.week > TOTAL_WEEKS && status.dayInWeek <= 6) {
            setIsComingSoon(true);
          }
        }
      }

      const stored = localStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        try {
          setInterruptedTask(JSON.parse(stored));
        } catch {
          localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
      setLoading(false);
    }
    init();
  }, []);

  const handleStartSession = useCallback((task: DailyTask) => {
    isRepeatModeRef.current = false;
    dailyGoalMetRef.current = false;
    setIgnorePerfectToday(false);
    setResumeSession(null);
    setActiveSession(task);
  }, []);

  const handleRepeatSession = useCallback((task: DailyTask) => {
    haptic(12);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setInterruptedTask(null);
    isRepeatModeRef.current = true;
    dailyGoalMetRef.current = true;
    setIgnorePerfectToday(true);
    setResumeSession(null);
    setActiveSession(task);
  }, []);

  const handleResumeSession = useCallback(() => {
    setInterruptedTask((interrupted) => {
      if (!interrupted) return null;
      const matchingTask: DailyTask =
        tasks.find((t) => t.id === interrupted.taskId) ?? {
          id: interrupted.taskId,
          task_type: interrupted.taskType,
          word_ids: interrupted.words.map((w) => w.id),
          is_completed: false,
          words: interrupted.words,
        };
      const sessionWords = interrupted.words.length > 0 ? interrupted.words : matchingTask.words;
      const dayStart = userSettings?.day_start_hour ?? 0;
      const replay = shouldResumeAsReplay(sessionWords, interrupted, dayStart);

      isRepeatModeRef.current = replay;
      dailyGoalMetRef.current = !!(interrupted.dailyGoalMet || matchingTask.is_completed);
      setIgnorePerfectToday(replay);
      setActiveSession({ ...matchingTask, words: sessionWords });
      setResumeSession(
        replay
          ? {
              ...interrupted,
              words: sessionWords,
              perfectWordIds: [],
              deckWordIds: sessionWords.map((w) => w.id),
              currentDeckIndex: 0,
              roundCount: 1,
              dailyGoalMet: dailyGoalMetRef.current,
              ignorePerfectToday: true,
            }
          : { ...interrupted, words: sessionWords, dailyGoalMet: dailyGoalMetRef.current },
      );
      return null;
    });
  }, [tasks, userSettings?.day_start_hour]);

  const activeSessionRef = useRef(activeSession);
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  const handleDailyGoalMet = useCallback(async (results: Record<string, SwipeResult>) => {
    if (isRepeatModeRef.current || dailyGoalMetRef.current) return;
    dailyGoalMetRef.current = true;
    const taskId = activeSessionRef.current?.id ?? "";
    try {
      await saveSessionResults(results, 30 * 60);
      if (taskId && !taskId.startsWith("fallback") && !taskId.startsWith("review-")) {
        await markTaskCompleted(taskId);
      }
      const [updated, settings] = await Promise.all([getDailyTasks(), getUserSettings()]);
      setTasks(updated);
      setUserSettings(settings);
    } catch (err) {
      console.error("[daily goal met]", err);
    }
  }, []);

  const handleSessionEnd = useCallback(async (results: Record<string, SwipeResult>) => {
    const isRepeat = isRepeatModeRef.current;
    const alreadyMet = dailyGoalMetRef.current;
    isRepeatModeRef.current = false;
    dailyGoalMetRef.current = false;
    try {
      await saveSessionResults(results, 30 * 60, { skipStreak: isRepeat || alreadyMet });
      if (!isRepeat && !alreadyMet) {
        const taskId = activeSessionRef.current?.id ?? "";
        if (taskId && !taskId.startsWith("fallback") && !taskId.startsWith("review-")) {
          await markTaskCompleted(taskId);
        }
      }
    } catch (err) {
      console.error("[session end]", err);
    }
    localStorage.removeItem(SESSION_STORAGE_KEY);
    if (!isRepeat) {
      const [updated, settings] = await Promise.all([getDailyTasks(), getUserSettings()]);
      setTasks(updated);
      setUserSettings(settings);
    }
    setInterruptedTask(null);
    setActiveSession(null);
    setResumeSession(null);
    setIgnorePerfectToday(false);
  }, []);

  const handleInterrupt = useCallback(() => {
    setActiveSession(null);
    setResumeSession(null);
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      try {
        setInterruptedTask(JSON.parse(stored));
      } catch {
        /* noop */
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (activeSession) {
    return (
      <StudySession
        taskId={activeSession.id}
        taskType={activeSession.task_type}
        words={activeSession.words}
        dayStartHour={userSettings?.day_start_hour ?? 0}
        initialState={resumeSession ?? undefined}
        ignorePerfectToday={ignorePerfectToday}
        onDailyGoalMet={handleDailyGoalMet}
        onSessionEnd={handleSessionEnd}
        onInterrupt={handleInterrupt}
      />
    );
  }

  const streak = userSettings?.current_streak ?? 0;
  const displayName = userSettings?.username?.trim() || "あなた";
  const weekDays = getCalendarWeekDays();

  return (
    <>
      <AnimatePresence>
        {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
      </AnimatePresence>

      <div
        className="bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-slate-100 pb-36"
        style={{ minHeight: "100dvh" }}
      >
        <nav className="px-5 pt-safe pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/30">
              <SpiralIcon className="w-4 h-6" color1="white" color2="white" pairColor="white" />
            </div>
            <span className="text-lg font-black tracking-tighter italic text-blue-600 dark:text-blue-400">
              locomoco
            </span>
          </div>
          <button
            onClick={() => navigate("/settings")}
            className="p-2.5 rounded-full active:bg-slate-200 dark:active:bg-slate-800 transition-colors"
          >
            <Settings size={20} className="text-slate-500" />
          </button>
        </nav>

        <section className="px-5 pt-2 pb-4 space-y-4">
          <h1 className="text-xl font-bold tracking-tight">{getGreeting(displayName)}</h1>

          <WeeklyStreakCard
            streak={streak}
            lastStudyDate={userSettings?.last_study_date ?? null}
            weekDays={weekDays}
          />

          <AnimatePresence>
            {interruptedTask && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="bg-blue-600 rounded-[1.5rem] p-5 shadow-lg shadow-blue-600/20"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-blue-100 text-xs font-bold mb-0.5">前回の続き</p>
                    <p className="text-white font-black text-lg">学習を再開しよう！</p>
                    <p className="text-blue-200 text-sm mt-0.5">
                      残り {Math.ceil(interruptedTask.remainingSeconds / 60)} 分
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      haptic(12);
                      handleResumeSession();
                    }}
                    className="shrink-0 flex items-center gap-1.5 bg-white text-blue-600 font-bold px-4 py-3 rounded-xl text-sm active:scale-95 transition-transform shadow"
                  >
                    <PlayCircle size={17} />
                    再開する
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        <section className="px-5 py-2">
          <h2 className="text-[10px] font-black text-slate-400 tracking-[0.3em] mb-4 px-1">
            今日のセッション
          </h2>
          <div className="space-y-3">
            {tasks.length === 0 ? (
              isRestDay ? (
                <div className="text-center py-10 bg-gradient-to-b from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-3xl border border-emerald-100 dark:border-emerald-900/50">
                  <div className="text-4xl mb-3">🌿</div>
                  <p className="text-emerald-700 dark:text-emerald-300 font-black text-lg">
                    今日は休憩日！
                  </p>
                  <p className="text-emerald-600/80 dark:text-emerald-400/80 text-sm mt-1.5 leading-relaxed px-6">
                    ストリークは自動で維持されました。
                    <br />
                    明日からは前週の総復習です！
                  </p>
                </div>
              ) : isComingSoon ? (
                <div className="text-center py-10 bg-gradient-to-b from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30 rounded-3xl border border-yellow-100 dark:border-yellow-900/50">
                  <div className="text-4xl mb-3">🏆</div>
                  <p className="text-yellow-700 dark:text-yellow-300 font-black text-lg">
                    {TOTAL_ENTRIES}語 制覇！
                  </p>
                  <p className="text-yellow-600/80 dark:text-yellow-400/80 text-sm mt-1.5 leading-relaxed px-6">
                    次の単語セットを準備中です。
                    <br />
                    7日目の復習セッションは引き続き続きます！
                  </p>
                  <div className="mt-4 mx-6 px-4 py-2.5 bg-yellow-100/60 dark:bg-yellow-900/30 rounded-xl">
                    <p className="text-yellow-600 dark:text-yellow-400 text-xs font-bold">
                      Coming Soon...
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-white dark:bg-slate-900/40 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
                  <p className="text-slate-500 font-medium">今日のタスクはありません</p>
                  <p className="text-slate-400 text-sm mt-1">また明日！</p>
                </div>
              )
            ) : (
              tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  disabled={!!interruptedTask}
                  onStart={() => handleStartSession(task)}
                  onRepeat={() => handleRepeatSession(task)}
                />
              ))
            )}
          </div>
        </section>

        <div className="fixed bottom-0 left-0 right-0 pb-safe px-6 bg-gradient-to-t from-slate-50 dark:from-[#020617] to-transparent pointer-events-none">
          <nav className="max-w-sm mx-auto bg-white/90 dark:bg-slate-900/90 backdrop-blur-2xl border border-slate-200 dark:border-slate-800 rounded-full p-1.5 flex items-center justify-around shadow-2xl pointer-events-auto">
            <button className="p-3 bg-blue-600 text-white rounded-full shadow-lg shadow-blue-600/30">
              <BookOpen size={22} />
            </button>
            <button
              onClick={() => {
                haptic(10);
                navigate("/progress");
              }}
              className="p-3 text-slate-400 hover:text-blue-500 active:scale-90 transition-all"
            >
              <Map size={22} />
            </button>
            <button
              onClick={() => {
                haptic(10);
                navigate("/settings");
              }}
              className="p-3 text-slate-400 hover:text-blue-500 active:scale-90 transition-all"
            >
              <Settings size={22} />
            </button>
          </nav>
        </div>
      </div>
    </>
  );
}

const WeeklyStreakCard = memo(function WeeklyStreakCard({
  streak,
  lastStudyDate,
  weekDays,
}: {
  streak: number;
  lastStudyDate: string | null;
  weekDays: { date: string; label: string }[];
}) {
  const today = localDate();
  const weekDateList = weekDays.map((d) => d.date);
  const studiedDates = getWeeklyStudiedDates(lastStudyDate, streak, weekDateList);
  const studiedSet = new Set(studiedDates);
  const studiedThisWeek = studiedDates.length;

  return (
    <div className="bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-[2rem] p-5 shadow-sm relative overflow-hidden">
      <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/8 blur-[60px] rounded-full -mr-10 -mt-10 pointer-events-none" />

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🔥</span>
          <div>
            <span className="text-3xl font-black text-orange-500 tabular-nums leading-none [font-family:var(--font-game)]">
              {streak}
            </span>
            <span className="text-sm font-bold text-orange-400 ml-1">日連続</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-black text-slate-400 tracking-widest">今週</p>
          <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
            {studiedThisWeek} / 7 日
          </p>
        </div>
      </div>

      <div className="flex items-center">
        {weekDays.map(({ date, label }, idx) => {
          const isStudied = studiedSet.has(date);
          const isToday = date === today;
          const isFuture = date > today;
          const prevStudied = idx > 0 && studiedSet.has(weekDays[idx - 1].date);
          const nextStudied = idx < 6 && studiedSet.has(weekDays[idx + 1].date);
          const chainLeft = isStudied && prevStudied;
          const chainRight = isStudied && nextStudied;

          return (
            <div key={date} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full flex items-center">
                {idx > 0 ? (
                  <div
                    className={`flex-1 h-[3px] rounded-full transition-colors ${
                      chainLeft ? "bg-orange-400" : "bg-slate-200 dark:bg-slate-700"
                    }`}
                  />
                ) : (
                  <div className="flex-1" />
                )}

                <motion.div
                  animate={isToday && !isStudied ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className={`relative shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black transition-all ${
                    isStudied
                      ? "bg-orange-500 text-white shadow-md shadow-orange-500/30"
                      : isToday
                      ? "bg-orange-100 dark:bg-orange-950/40 text-orange-500 border-2 border-orange-400 border-dashed"
                      : isFuture
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-400"
                  }`}
                >
                  {isStudied ? "🔥" : isToday ? "今" : ""}
                </motion.div>

                {idx < 6 ? (
                  <div
                    className={`flex-1 h-[3px] rounded-full transition-colors ${
                      chainRight ? "bg-orange-400" : "bg-slate-200 dark:bg-slate-700"
                    }`}
                  />
                ) : (
                  <div className="flex-1" />
                )}
              </div>

              <span
                className={`text-[10px] font-bold leading-none ${
                  isToday || isStudied ? "text-orange-500" : "text-slate-400 dark:text-slate-600"
                }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

const TaskCard = memo(function TaskCard({
  task,
  onStart,
  onRepeat,
  disabled,
}: {
  task: DailyTask;
  onStart: () => void;
  onRepeat: () => void;
  disabled?: boolean;
}) {
  const icons = {
    LEARNING: <BookOpen size={24} />,
    REVIEW_G: <RotateCcw size={24} />,
    REVIEW_R: <Zap size={24} />,
  };
  const colors = {
    LEARNING: "bg-blue-600 shadow-blue-500/20",
    REVIEW_G: "bg-indigo-600 shadow-indigo-500/20",
    REVIEW_R: "bg-cyan-600 shadow-cyan-500/20",
  };
  const labels = {
    LEARNING: "デイリースパイラル",
    REVIEW_G: "スパイラルレビュー",
    REVIEW_R: "弱点集中トレーニング",
  };

  const canStart = !task.is_completed && !disabled;

  if (task.is_completed) {
    return (
      <div className="w-full bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 p-5 rounded-[1.75rem] flex items-center gap-4 shadow-sm">
        <div
          className={`w-[52px] h-[52px] rounded-2xl flex items-center justify-center text-white shadow-lg ${colors[task.task_type]}`}
        >
          {icons[task.task_type]}
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-base mb-0.5">{labels[task.task_type]}</h3>
          <p className="text-sm text-emerald-500 font-bold">✓ 今日の学習完了！</p>
        </div>
        <button
          onClick={onRepeat}
          className="shrink-0 flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold text-xs px-3 py-2 rounded-xl active:scale-95 transition-transform"
        >
          <RotateCcw size={13} />
          もう一周
        </button>
      </div>
    );
  }

  return (
    <motion.button
      whileTap={canStart ? { scale: 0.985 } : {}}
      disabled={!canStart}
      onClick={() => {
        if (canStart) {
          haptic(15);
          onStart();
        }
      }}
      className={`w-full text-left bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 p-5 rounded-[1.75rem] flex items-center gap-4 shadow-sm transition-all ${
        disabled ? "opacity-40 grayscale" : "group"
      }`}
    >
      <div
        className={`w-[52px] h-[52px] rounded-2xl flex items-center justify-center text-white shadow-lg ${colors[task.task_type]}`}
      >
        {icons[task.task_type]}
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-base mb-0.5">{labels[task.task_type]}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {disabled ? "↑ 先に前回の続きを再開してね" : `${task.words.length}単語 · 30分`}
        </p>
      </div>
      {!disabled && (
        <div className="shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center">
          <PlayCircle size={20} />
        </div>
      )}
    </motion.button>
  );
});
